/**
 * V2 Banner (Alert) — the `.banner` treatment from design_reference,
 * extended to ok / info / warn tones following the same color-mix pattern.
 */

import type { ReactNode } from 'react';
import { cn } from '../../../lib/utils';

/** Banner tone. */
export type BannerTone = 'ok' | 'info' | 'warn';

const TONE_CLASS: Record<BannerTone, string> = {
  ok: 'bg-[var(--ok-bg)] border-[color-mix(in_oklab,var(--ok)_28%,transparent)] [&>.banner-ico]:text-[var(--ok)]',
  info: 'bg-[var(--info-bg)] border-[color-mix(in_oklab,var(--info)_28%,transparent)] [&>.banner-ico]:text-[var(--info)]',
  warn: 'bg-[var(--warn-bg)] border-[color-mix(in_oklab,var(--warn)_28%,transparent)] [&>.banner-ico]:text-[var(--warn)]',
};

/** Props for the V2 Banner. */
export interface BannerProps {
  tone?: BannerTone;
  /** Leading icon node (tinted to the tone). */
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** V2 inline banner / alert. */
export function Banner({ tone = 'info', icon, children, className }: BannerProps) {
  return (
    <div
      role="status"
      className={cn(
        'flex gap-[11px] items-start p-[12px_14px] rounded-[var(--r-md)] border text-[var(--tx-hi)]',
        TONE_CLASS[tone],
        className,
      )}
    >
      {icon && (
        <span className="banner-ico shrink-0 mt-px [&_svg]:w-[17px] [&_svg]:h-[17px]">{icon}</span>
      )}
      <div className="min-w-0 text-[12.5px]">{children}</div>
    </div>
  );
}
